/* eslint-disable max-len */
import { Link } from 'react-router-dom';
import { Box, HStack, Stack, Table, Text, VStack } from '@chakra-ui/react';
import { ReactComponent as Deposit } from 'assets/icons/deposit.svg';
import { ReactComponent as ExchangeIcon } from 'assets/icons/exchange.svg';
import { ReactComponent as HistoryIcon } from 'assets/icons/history.svg';
import { ReactComponent as Withdraw } from 'assets/icons/withdraw.svg';
import { useAccess, useWallets } from 'hooks';
import { useTranslation } from 'localization';
import { routes } from 'statics';

import { SERVICE_FLOW_BORDER, Spinner } from 'components';
import { fixDecimal } from 'utils';

const WalletsTab: React.FC = () => {
  const { t } = useTranslation('component.wallet.tabs.wallets');
  const { data: wallets, isLoading } = useWallets({ queries: {} });

  const access = useAccess();

  const disabledDepositOrWithdraw = [
    'MATIC',
    'MKR',
    'BAT',
    'CRV',
    'SUI',
    'BCH',
    'YFI',
    'ADA',
    'DAI',
    'LINK',
    'SUSHI',
    'ZEC',
    'COMP',
    'XRP',
    'SOL',
    'SHIB',
    'LTC',
    'USDC',
    'UNI',
    'GRT',
    'AAVE',
    'SNX',
  ];

  const disabledExchange = ['BTC', 'ETH'];

  return (
    <VStack mx={{ base: 4, lg: 0 }}>
      <Box
        w="100%"
        my={4}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
      >
        <Text
          fontWeight="500"
          fontSize="var(--chakra-font-sizes-3xl)"
          color="body"
        >
          {t('allWallets')}
        </Text>
      </Box>
      <Box w="100%" display={{ base: 'none', xl: 'block' }}>
        {isLoading && (
          <HStack justify="center" w="100%">
            <Spinner />
          </HStack>
        )}
        {wallets && wallets.list?.length > 0 && (
          <Box
            w="100%"
            rounded="md"
            borderWidth="1px"
            borderColor={SERVICE_FLOW_BORDER}
            bg="dark"
            overflow="hidden"
          >
            <Table.Root size="sm" bg="dark" color="body" variant="line">
              <Table.Header>
                <Table.Row
                  bg="dark"
                  borderBottomWidth="1px"
                  borderBottomColor={SERVICE_FLOW_BORDER}
                  _hover={{ bg: 'dark' }}
                >
                  <Table.ColumnHeader
                    color="body2"
                    borderWidth={0}
                    py={3}
                    px={1}
                    fontSize="var(--chakra-font-sizes-sm)"
                    fontWeight="medium"
                  >
                    {t('currency')}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader
                    textAlign="end"
                    color="body2"
                    borderWidth={0}
                    py={3}
                    px={1}
                    fontSize="var(--chakra-font-sizes-sm)"
                    fontWeight="medium"
                  >
                    {t('freezed')}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader
                    textAlign="end"
                    color="body2"
                    borderWidth={0}
                    py={3}
                    px={1}
                    fontSize="var(--chakra-font-sizes-sm)"
                    fontWeight="medium"
                  >
                    {t('amount')}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader
                    textAlign="end"
                    color="body2"
                    borderWidth={0}
                    py={3}
                    pl={1}
                    pr={0}
                    fontSize="var(--chakra-font-sizes-sm)"
                    fontWeight="medium"
                  >
                    {t('actions')}
                  </Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {wallets?.list
                  ?.sort((a, b) =>
                    a.currency.symbol === 'USDT'
                      ? -1
                      : b.currency.symbol === 'USDT'
                        ? 1
                        : a.currency.symbol.localeCompare(b.currency.symbol)
                  )
                  ?.sort((a, b) => b.amount - a.amount)
                  .map((wallet) => {
                    return (
                      <Table.Row
                        key={wallet.id}
                        bg="dark"
                        borderBottomWidth="1px"
                        borderBottomColor={SERVICE_FLOW_BORDER}
                        transition="background 0.15s ease"
                        _hover={{ bg: 'rgba(255, 255, 255, 0.04)' }}
                      >
                        <Table.Cell
                          borderWidth={0}
                          py={3}
                          px={1}
                          verticalAlign="middle"
                        >
                          <Stack flexDir="row" align="center" gap={3}>
                            <Box
                              w="36px"
                              h="36px"
                              borderRadius="full"
                              overflow="hidden"
                              flexShrink={0}
                            >
                              <img
                                src={wallet.currency.icon}
                                alt={wallet.currency.title}
                                width="36"
                                height="36"
                              />
                            </Box>
                            <Text
                              color="body"
                              fontSize="var(--chakra-font-sizes-sm)"
                              fontWeight="medium"
                            >
                              {wallet.currency.title}
                            </Text>
                          </Stack>
                        </Table.Cell>
                        <Table.Cell
                          textAlign="end"
                          borderWidth={0}
                          py={3}
                          px={1}
                          color="body"
                          fontSize="var(--chakra-font-sizes-sm)"
                          fontVariantNumeric="tabular-nums"
                        >
                          {fixDecimal({
                            value: wallet.frozen_amount,
                            fractionDigits: wallet.type === 'CRYPTO' ? 5 : 2,
                          })}
                        </Table.Cell>
                        <Table.Cell
                          textAlign="end"
                          borderWidth={0}
                          py={3}
                          px={1}
                          color="body"
                          fontSize="var(--chakra-font-sizes-sm)"
                          fontWeight="medium"
                          fontVariantNumeric="tabular-nums"
                        >
                          {fixDecimal({
                            value: wallet.amount - wallet.frozen_amount,
                            fractionDigits: wallet.type === 'CRYPTO' ? 5 : 2,
                          })}
                        </Table.Cell>
                        <Table.Cell borderWidth={0} py={3} pl={1} pr={0}>
                          <HStack align="center" justify="flex-end" gap={0}>
                            {!disabledExchange.includes(
                              wallet.currency.symbol
                            ) && (
                              <Link
                                to={`${routes.exchange.url}?wallet=${wallet.id}`}
                              >
                                <Box
                                  display="inline-flex"
                                  borderRadius="md"
                                  p={1.5}
                                  transition="background 0.15s ease"
                                  _hover={{ bg: 'rgba(255, 255, 255, 0.08)' }}
                                >
                                  <ExchangeIcon />
                                </Box>
                              </Link>
                            )}
                            {!disabledDepositOrWithdraw.includes(
                              wallet.currency.symbol
                            ) &&
                              access.withdrawService && (
                                <Link
                                  to={`${routes.withdraw.url}?tab=0&wallet=${wallet.id}&currency=${wallet.currency.id}`}
                                >
                                  <Box
                                    display="inline-flex"
                                    borderRadius="md"
                                    p={1.5}
                                    transition="background 0.15s ease"
                                    _hover={{ bg: 'rgba(255, 255, 255, 0.08)' }}
                                  >
                                    <Withdraw cursor="pointer" />
                                  </Box>
                                </Link>
                              )}
                            {!disabledDepositOrWithdraw.includes(
                              wallet.currency.symbol
                            ) && (
                              <Link
                                to={`${routes.deposit.url}?tab=0&wallet=${wallet.id}&currency=${wallet.currency.id}`}
                              >
                                <Box
                                  display="inline-flex"
                                  borderRadius="md"
                                  p={1.5}
                                  transition="background 0.15s ease"
                                  _hover={{ bg: 'rgba(255, 255, 255, 0.08)' }}
                                >
                                  <Deposit cursor="pointer" />
                                </Box>
                              </Link>
                            )}
                            <Link
                              to={`${routes.wallets.url}?tab=transaction&wallet=${wallet.currency.id}`}
                            >
                              <Box
                                display="inline-flex"
                                borderRadius="md"
                                p={1.5}
                                transition="background 0.15s ease"
                                _hover={{ bg: 'rgba(255, 255, 255, 0.08)' }}
                              >
                                <HistoryIcon />
                              </Box>
                            </Link>
                          </HStack>
                        </Table.Cell>
                      </Table.Row>
                    );
                  })}
              </Table.Body>
            </Table.Root>
          </Box>
        )}
      </Box>
      <VStack
        w="100%"
        display={{ xl: 'none' }}
        align="stretch"
        gap={0}
        borderTopWidth="1px"
        borderTopColor="rgba(255,255,255,0.08)"
      >
        {wallets?.list
          ?.sort((a, b) => b.amount - a.amount)
          ?.map((wallet) => {
            return (
              <VStack
                key={wallet.id}
                w="100%"
                align="stretch"
                gap={3}
                py={4}
                borderBottomWidth="1px"
                borderBottomColor="rgba(255,255,255,0.08)"
              >
                <HStack w="100%" justify="space-between" align="center">
                  <HStack gap={3}>
                    <Box
                      w="36px"
                      h="36px"
                      borderRadius="full"
                      overflow="hidden"
                      flexShrink={0}
                    >
                      <img
                        src={wallet.currency.icon}
                        alt={wallet.currency.symbol}
                        width="36"
                        height="36"
                      />
                    </Box>
                    <Text
                      fontSize="var(--chakra-font-sizes-sm)"
                      fontWeight="medium"
                      color="body"
                    >
                      {wallet.currency.symbol}
                    </Text>
                  </HStack>
                </HStack>
                <HStack w="100%" justify="space-between" align="center">
                  <Text fontSize="var(--chakra-font-sizes-sm)" color="body2">
                    Frozen Balance
                  </Text>
                  <Text
                    fontSize="var(--chakra-font-sizes-sm)"
                    fontWeight="medium"
                    color="body"
                    fontVariantNumeric="tabular-nums"
                  >
                    {fixDecimal({
                      value: wallet.frozen_amount,
                      fractionDigits: wallet.type === 'CRYPTO' ? 5 : 2,
                    })}
                  </Text>
                </HStack>
                <HStack w="100%" justify="space-between" align="center">
                  <Text fontSize="var(--chakra-font-sizes-sm)" color="body2">
                    Amount Balance
                  </Text>
                  <Text
                    fontSize="var(--chakra-font-sizes-sm)"
                    fontWeight="medium"
                    color="body"
                    fontVariantNumeric="tabular-nums"
                  >
                    {fixDecimal({
                      value: wallet.amount,
                      fractionDigits: wallet.type === 'CRYPTO' ? 5 : 2,
                    })}
                  </Text>
                </HStack>
                <HStack w="100%" justify="flex-end" align="center" gap={0}>
                  {!disabledExchange.includes(wallet.currency.symbol) && (
                    <Link to={`${routes.exchange.url}?wallet=${wallet.id}`}>
                      <Box
                        display="inline-flex"
                        borderRadius="md"
                        p={1.5}
                        _hover={{ bg: 'rgba(255, 255, 255, 0.08)' }}
                      >
                        <ExchangeIcon />
                      </Box>
                    </Link>
                  )}
                  {!disabledDepositOrWithdraw.includes(
                    wallet.currency.symbol
                  ) &&
                    access.withdrawService && (
                      <Link
                        to={`${routes.withdraw.url}?tab=0&wallet=${wallet.id}&currency=${wallet.currency.id}`}
                      >
                        <Box
                          display="inline-flex"
                          borderRadius="md"
                          p={1.5}
                          _hover={{ bg: 'rgba(255, 255, 255, 0.08)' }}
                        >
                          <Withdraw cursor="pointer" />
                        </Box>
                      </Link>
                    )}
                  {!disabledDepositOrWithdraw.includes(
                    wallet.currency.symbol
                  ) && (
                    <Link
                      to={`${routes.deposit.url}?tab=0&wallet=${wallet.id}&currency=${wallet.currency.id}`}
                    >
                      <Box
                        display="inline-flex"
                        borderRadius="md"
                        p={1.5}
                        _hover={{ bg: 'rgba(255, 255, 255, 0.08)' }}
                      >
                        <Deposit cursor="pointer" />
                      </Box>
                    </Link>
                  )}
                  <Link
                    to={`${routes.wallets.url}?tab=transaction&wallet=${wallet.currency.id}`}
                  >
                    <Box
                      display="inline-flex"
                      borderRadius="md"
                      p={1.5}
                      _hover={{ bg: 'rgba(255, 255, 255, 0.08)' }}
                    >
                      <HistoryIcon />
                    </Box>
                  </Link>
                </HStack>
              </VStack>
            );
          })}
      </VStack>
    </VStack>
  );
};

export default WalletsTab;
